import { useEffect, useState } from "react";
import {
  supabaseAuthenticatedApi,
  workspaceAgentUrl,
} from "../components/SupabaseClient";

export function useRemoteFileContent(source, filePath, metadata, options = {}) {
  const { forceOpen = false } = options;
  const [contentData, setContentData] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!source || !filePath || !metadata) {
      setContentData(null);
      setStatus("idle");
      setError(null);
      return;
    }

    const canForceOpen = metadata.allowForceOpen === true;
    const previewAllowed =
      metadata.previewAllowed === true || (canForceOpen && forceOpen);

    if (!previewAllowed) {
      setContentData(null);
      setStatus("blocked");
      setError(null);
      return;
    }

    let active = true;
    setContentData(null);
    setStatus("loading");
    setError(null);

    const loadContent = async () => {
      try {
        const response = await supabaseAuthenticatedApi.post(
          "/filesystem/read-file-content",
          { scope: source, path: filePath, force: forceOpen },
          workspaceAgentUrl,
          10000,
        );
        const data = await response.json();
        if (!active) return;

        setContentData({
          content:
            typeof data.content === "string"
              ? data.content
              : Array.isArray(data.content)
                ? data.content.join("\n")
                : "",
          lineCount:
            typeof data.line_count === "number" ? data.line_count : null,
          warning: data.warning || null,
        });
        setStatus("ready");
      } catch (err) {
        if (!active) return;
        console.error(
          "[useRemoteFileContent] Failed to fetch file content:",
          err,
        );
        setContentData(null);
        setError(err.message || "Failed to load file content.");
        setStatus("error");
      }
    };

    loadContent();

    return () => {
      active = false;
    };
  }, [filePath, forceOpen, metadata, source]);

  return {
    contentData,
    error,
    status,
    warning:
      status === "blocked"
        ? metadata?.previewDenialReason || null
        : contentData?.warning || null,
    canForceOpen: status === "blocked" && metadata?.allowForceOpen === true,
  };
}
